import { useMemo } from 'react';

import { accountStatusOptions } from '@/constants';
import { TOG_90_DAYS_PAST_DUE } from '@/pages/donor-scheduling-partnership/constants';

export type PastDueOwner = {
  aging60?: boolean | null;
  aging180?: boolean | null;
  aging365?: boolean | null;
  accountStatus?: number | null;
  tog90daysPastDue?: number | null;
  tog90DayOverrideExpireDate?: string | null;
};

export type PastDuePartnership = {
  account1?: PastDueOwner | null;
  account2?: PastDueOwner | null;
  account3?: PastDueOwner | null;
  account4?: PastDueOwner | null;
};

export type PastDueStatusResult = {
  isPastDue: boolean; // Owner or any partner is past due
  ownerPastDue: boolean;
  partnersPastDue: boolean; //at least one partner is past due
  hasAging60: boolean;
  hasOver90Days: boolean;
  isLocked: boolean;
  overrideActiveUntil: string | null;
};

export const getClosedNoNewBusinessValue = (): number | null =>
  accountStatusOptions.find((opt) => opt.label === 'Closed - No New Business')
    ?.value ??
  accountStatusOptions.find((opt) => opt.value === 100000003)?.value ??
  null;

export const parseLocalDate = (dateStr: string): Date => {
  const [y, m, d] = dateStr.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
};

const getPartnersFlat = (partnerships: PastDuePartnership[]): PastDueOwner[] =>
  partnerships.flatMap(
    (p) =>
      [p.account1, p.account2, p.account3, p.account4].filter(
        Boolean,
      ) as PastDueOwner[],
  );

const isAccountPastDue = (
  account: PastDueOwner | null | undefined,
  closedValue: number | null,
): boolean =>
  !!(
    account?.aging60 ||
    account?.aging180 ||
    account?.aging365 ||
    (closedValue !== null && account?.accountStatus === closedValue)
  );

const isAccountOver90 = (
  account: PastDueOwner | null | undefined,
  closedValue: number | null,
): boolean =>
  !!(
    account?.aging180 ||
    account?.aging365 ||
    (closedValue !== null && account?.accountStatus === closedValue)
  );

export const getPastDueStatus = (
  owner: PastDueOwner | null | undefined,
  partnerships: PastDuePartnership[],
  closedNoNewBusinessValue: number | null,
  skipPartnerCheck = false,
): PastDueStatusResult => {
  const allPartners = skipPartnerCheck ? [] : getPartnersFlat(partnerships);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const ownerPastDue = isAccountPastDue(owner, closedNoNewBusinessValue);
  const ownerHasOver90 = isAccountOver90(owner, closedNoNewBusinessValue);
  const partnersPastDue = allPartners.some((p) =>
    isAccountPastDue(p, closedNoNewBusinessValue),
  );
  const partnersHaveOver90 = allPartners.some((p) =>
    isAccountOver90(p, closedNoNewBusinessValue),
  );

  const ownerHasAging60 = !!owner?.aging60;
  const partnersHaveAging60 = allPartners.some((p) => p.aging60);

  const ownerTog90Locked =
    owner?.tog90daysPastDue === TOG_90_DAYS_PAST_DUE.YES ||
    owner?.tog90daysPastDue === TOG_90_DAYS_PAST_DUE.CLOSED;

  const partnersTog90Locked = allPartners.some(
    (p) =>
      p.tog90daysPastDue === TOG_90_DAYS_PAST_DUE.YES ||
      p.tog90daysPastDue === TOG_90_DAYS_PAST_DUE.CLOSED,
  );

  const partnersRestricted = allPartners.some(
    (p) =>
      (p.aging180 || p.aging365) &&
      (p.tog90daysPastDue === TOG_90_DAYS_PAST_DUE.YES ||
        p.tog90daysPastDue === TOG_90_DAYS_PAST_DUE.CLOSED),
  );

  const partnersOverrideExpired = allPartners.some(
    (p) =>
      (p.aging180 || p.aging365) &&
      p.tog90daysPastDue === TOG_90_DAYS_PAST_DUE.NO &&
      p.tog90DayOverrideExpireDate &&
      today > parseLocalDate(p.tog90DayOverrideExpireDate),
  );

  const hasOver90Days = ownerHasOver90 || partnersHaveOver90;
  const isPastDue = ownerPastDue || partnersPastDue;
  const hasAging60 = ownerHasAging60 || partnersHaveAging60;

  if (!hasOver90Days) {
    if (ownerTog90Locked || partnersTog90Locked) {
      return {
        isPastDue: true,
        ownerPastDue,
        partnersPastDue,
        hasAging60,
        hasOver90Days: false,
        isLocked: true,
        overrideActiveUntil: null,
      };
    }
    return {
      isPastDue,
      ownerPastDue,
      partnersPastDue,
      hasAging60,
      hasOver90Days: false,
      isLocked: false,
      overrideActiveUntil: null,
    };
  }

  const tog90DaysPastDueValue = owner?.tog90daysPastDue;
  const tog90DayOverrideExpireDate = owner?.tog90DayOverrideExpireDate;

  if (
    partnersRestricted ||
    partnersOverrideExpired ||
    tog90DaysPastDueValue === TOG_90_DAYS_PAST_DUE.YES ||
    tog90DaysPastDueValue === TOG_90_DAYS_PAST_DUE.CLOSED
  ) {
    return {
      isPastDue,
      ownerPastDue,
      partnersPastDue,
      hasAging60,
      hasOver90Days: true,
      isLocked: true,
      overrideActiveUntil: null,
    };
  }

  if (
    tog90DaysPastDueValue === TOG_90_DAYS_PAST_DUE.NO &&
    tog90DayOverrideExpireDate
  ) {
    const expireDate = parseLocalDate(tog90DayOverrideExpireDate);

    if (today > expireDate) {
      return {
        isPastDue,
        ownerPastDue,
        partnersPastDue,
        hasAging60,
        hasOver90Days: true,
        isLocked: true,
        overrideActiveUntil: null,
      };
    }

    return {
      isPastDue,
      ownerPastDue,
      partnersPastDue,
      hasAging60,
      hasOver90Days: true,
      isLocked: false,
      overrideActiveUntil: expireDate.toLocaleDateString(),
    };
  }

  return {
    isPastDue,
    ownerPastDue,
    partnersPastDue,
    hasAging60,
    hasOver90Days: true,
    isLocked: false,
    overrideActiveUntil: null,
  };
};

export const usePastDueStatus = (
  owner: PastDueOwner | null | undefined,
  partnerships: PastDuePartnership[],
  skipPartnerCheck = false,
): PastDueStatusResult => {
  const closedNoNewBusinessValue = useMemo(getClosedNoNewBusinessValue, []);

  return useMemo(
    () =>
      getPastDueStatus(
        owner,
        partnerships,
        closedNoNewBusinessValue,
        skipPartnerCheck,
      ),
    [
      owner?.aging60,
      owner?.aging180,
      owner?.aging365,
      owner?.accountStatus,
      owner?.tog90daysPastDue,
      owner?.tog90DayOverrideExpireDate,
      partnerships,
      closedNoNewBusinessValue,
      skipPartnerCheck,
    ],
  );
};
